import { useDispatch } from 'react-redux'
import {
  useLazyGetAppCapabilitiesQuery,
  useLazyGetRoleCapabilitiesQuery,
} from '../../api/app/UserManagementApiService'
import { useEffect, useState } from 'react'
import {
  FeatureGroup,
  LevelOneState,
  LevelThreeState,
  LevelTwoState,
} from '../../utils/types/roles/RolesTypes'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/Store'
import {
  addCapabilityDetails,
  removeCapabilityDetails,
  replaceCapabilityDetails,
  resetCapabilityDetails,
} from '../../redux/reducers/rolesManagementReducer'

export const useSelectCapabilities = () => {
  const dispatch = useDispatch()
  const [getAppCapabilities, appCapabilities] = useLazyGetAppCapabilitiesQuery()
  const [getRoleCapabilities, roleCapabilities] =
    useLazyGetRoleCapabilitiesQuery()
  const [capabilityListData, setCapabilityListData] = useState<FeatureGroup[]>(
    [],
  )

  const capabilitiesFinalResult = useSelector(
    (state: RootState) => state.roleCapabilities.capabilityDetails,
  )
  const roleDetails = useSelector(
    (state: RootState) => state.roleCapabilities.roleDetails,
  )

  const [levelOneState, setLevelOneState] = useState<LevelOneState>({
    options: [],
    activeFeature: '',
  })
  const [levelTwoState, setLevelTwoState] = useState<LevelTwoState>({
    options: [],
    activeFeature: '',
  })
  const [levelThreeState, setLevelThreeState] = useState<LevelThreeState>({
    options: [],
  })
  useEffect(() => {
    if (roleDetails?.applicationTypeCode) {
      getAppCapabilities(roleDetails?.applicationTypeCode)
      //Get Role Capabilities when Copy Capabilities is selected and when it is not edit role
      if (roleDetails?.role !== '' && !roleDetails?.isEdit) {
        getRoleCapabilities(roleDetails?.role)
      } else if (roleDetails?.role === '') {
        dispatch(resetCapabilityDetails())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleDetails])

  useEffect(() => {
    if (capabilityListData?.length > 0) {
      // initial state update with the first item of Capability List
      const levelOneData = capabilityListData
      const levelTwoData = levelOneData[0]?.features || []
      const levelThreeData = levelTwoData[0]?.capabilities || []
      setLevelOneState({
        ...levelOneState,
        options: levelOneData,
        activeFeature: levelOneData[0].featureGroupCode,
      })
      setLevelTwoState({
        ...levelTwoState,
        options: levelTwoData,
        activeFeature: levelTwoData[0].featureCode,
      })
      setLevelThreeState({
        ...levelThreeState,
        options: levelThreeData,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilityListData])

  useEffect(() => {
    if (appCapabilities?.data?.length > 0) {
      setCapabilityListData(appCapabilities?.data)
    }
  }, [appCapabilities])

  useEffect(() => {
    if (roleCapabilities?.data) {
      dispatch(
        replaceCapabilityDetails(roleCapabilities?.data?.capabilities ?? []),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCapabilities])

  const isFeaturePresent = (
    data: FeatureGroup[],
    level: 1 | 2 | 3,
    code: string,
  ): boolean => {
    if (!data?.length) return false

    // if (data?.length > 0) {
    if (level === 1) {
      return data.some((item) => item?.featureGroupCode === code)
    } else if (level === 2) {
      return data.some((item) =>
        item.features?.some((subItem) => subItem.featureCode === code),
      )
    } else if (level === 3) {
      return data.some((item) =>
        item.features?.some((subItem) =>
          subItem.capabilities?.some(
            (leafItem) => leafItem.capabilityCode === code,
          ),
        ),
      )
    }
    return false
  }

  const updateLevelOneActiveFeature = (featureCode: string): void => {
    if (!featureCode) return

    const updatedLevelOneData = capabilityListData.find(
      (item) => item.featureGroupCode === featureCode,
    )
    const updatedLevelTwoData = updatedLevelOneData?.features || []
    const updatedLevelThreeData = updatedLevelTwoData[0]?.capabilities || []

    setLevelOneState({
      options: capabilityListData,
      activeFeature: featureCode,
    })

    setLevelTwoState({
      options: updatedLevelTwoData,
      activeFeature: updatedLevelTwoData[0]?.featureCode || '',
    })

    setLevelThreeState({
      options: updatedLevelThreeData,
    })
  }

  const updateLevelTwoActiveFeature = (featureCode: string): void => {
    const updatedLevelThreeData =
      levelTwoState.options.find((item) => item.featureCode === featureCode)
        ?.capabilities || []

    setLevelTwoState({
      ...levelTwoState,
      activeFeature: featureCode,
    })

    setLevelThreeState({
      options: updatedLevelThreeData,
    })
  }

  // Checkbox handlers
  const handleLevelOneCheckboxSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    featureCode: string,
  ) => {
    if (e.target.checked) {
      dispatch(
        addCapabilityDetails({
          newItem: { featureGroupCode: featureCode },
          level: 'root',
          parentCode: '',
        }),
      )
    } else {
      dispatch(
        removeCapabilityDetails({
          level: 'root',
          featureCode: featureCode,
        }),
      )
    }
  }

  const handleLevelTwoCheckboxSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    featureCode: string,
  ) => {
    if (e.target.checked) {
      dispatch(
        addCapabilityDetails({
          newItem: { featureCode: featureCode },
          level: 'sub',
          parentCode: levelOneState?.activeFeature,
        }),
      )
    } else {
      dispatch(
        removeCapabilityDetails({
          level: 'sub',
          featureCode: featureCode,
        }),
      )
    }
  }

  const handleLevelThreeCheckboxSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    featureCode: string,
  ) => {
    const capabilityData = JSON.parse(e?.target?.id)
    const newItem = {
      capabilityCode: capabilityData.capabilityCode,
      enableRoleMapping: capabilityData.enableRoleMapping,
    }
    if (e.target.checked) {
      dispatch(
        addCapabilityDetails({
          newItem: newItem,
          level: 'leaf',
          parentCode: levelTwoState?.activeFeature,
        }),
      )
    } else {
      dispatch(
        removeCapabilityDetails({
          level: 'leaf',
          featureCode: featureCode,
        }),
      )
    }
  }

  const getCheckboxDisableStatus = (
    level: 1 | 2 | 3,
    activeFeature: string,
  ) => {
    return isFeaturePresent(capabilitiesFinalResult, level, activeFeature)
  }
  return {
    roleDetails,
    levelOneState,
    levelTwoState,
    levelThreeState,
    capabilitiesFinalResult,
    appCapabilities,
    isFeaturePresent,
    updateLevelOneActiveFeature,
    updateLevelTwoActiveFeature,
    handleLevelOneCheckboxSelection,
    handleLevelTwoCheckboxSelection,
    handleLevelThreeCheckboxSelection,
    getCheckboxDisableStatus,
  }
}
